
import React, {
    useEffect,
    useRef
} from "react";

import * as am4core
    from "@amcharts/amcharts4/core";

import * as am4charts
    from "@amcharts/amcharts4/charts";


//========================================================
// OMNISCAN PRO
// HOME INTELLIGENCE CHART
//
// Shared amCharts v4 Renderer
//
// Used by:
// - Home Data Carousel
// - Executive Intelligence / View All
//
// Supported Chart Types:
// - Total   : Doughnut
// - Stack   : Stacked Columns
// - Trend   : Monthly Line
// - Top Ten : Horizontal Bars
// - Spider  : Radar
// - Funnel  : Funnel
//
// Uses amCharts v4 APIs throughout.
//========================================================


//========================================================
// Colour Helper
//========================================================

function chartColour(color)
{
    return am4core.color(color || "#2686cf");
}


//========================================================
// Premium Bar Gradient
//
// amCharts v4 native LinearGradient.
//
// Subtle highlight using shades of the subject colour.
// No white or grey bands.
//========================================================

function createBarGradient(color)
{
    const gradient = new am4core.LinearGradient();

    gradient.rotation = 90;

    //----------------------------------------------------
    // Subject-specific colour stops
    //----------------------------------------------------

    const gradients =
    {
        "#7735C5": [
            "#A879E1",
            "#7735C5",
            "#572594"
        ],

        "#E49A35": [
            "#F1BD70",
            "#E49A35",
            "#B87520"
        ],

        "#28A788": [
            "#69CDB3",
            "#28A788",
            "#197C65"
        ],

        "#D65B64": [
            "#EC939B",
            "#D65B64",
            "#A83B47"
        ]
    };

    //----------------------------------------------------
    // Find matching palette
    //----------------------------------------------------

    const shades =
        gradients[String(color).toUpperCase()] ||
        [color, color, color];

    //----------------------------------------------------
    // Build v4 gradient
    //----------------------------------------------------

    gradient.addColor(
        am4core.color(shades[0]),
        1,
        0
    );

    gradient.addColor(
        am4core.color(shades[1]),
        1,
        0.45
    );

    gradient.addColor(
        am4core.color(shades[2]),
        1,
        1
    );

    return gradient;
}


//========================================================
// Shared Chart Configuration
//========================================================

function configureChart(chart)
{
    chart.padding(8, 8, 8, 8);

    chart.background.fillOpacity = 0;

    if(chart.logo)
    {
        chart.logo.disabled = true;
    }
}


//========================================================
// Shared Tooltip Styling
//========================================================

function configureTooltip(tooltip)
{
    if(!tooltip)
    {
        return;
    }

    tooltip.getFillFromObject = false;

    tooltip.background.fill =
        am4core.color("#20272d");

    tooltip.background.strokeOpacity = 0;

    tooltip.background.cornerRadius = 4;

    tooltip.label.fill =
        am4core.color("#ffffff");

    tooltip.label.fontSize = 11;

    tooltip.label.fontWeight = "500";
}


//========================================================
// Shared Category Axis Styling
//========================================================

function configureCategoryAxis(axis)
{
    axis.renderer.grid.template.disabled = true;

    axis.renderer.labels.template.fill =
        am4core.color("#435466");

    axis.renderer.labels.template.fontSize = 10;

    axis.renderer.labels.template.fontWeight = "500";

    axis.renderer.labels.template.truncate = true;

    axis.renderer.labels.template.maxWidth = 95;

    axis.renderer.minGridDistance = 25;

    axis.cursorTooltipEnabled = false;
}


//========================================================
// Shared Value Axis Styling
//========================================================

function configureValueAxis(axis)
{
    axis.min = 0;

	//========================================================
	// Hide Value Axis Grid Lines
	//========================================================

	axis.renderer.grid.template.disabled = true;

    axis.renderer.labels.template.fill =
        am4core.color("#657688");

    axis.renderer.labels.template.fontSize = 10;

    axis.cursorTooltipEnabled = false;
}


//========================================================
// Total - Doughnut Chart
//========================================================

function createTotalChart(container, data, color)
{
    const chart = am4core.create(
        container,
        am4charts.PieChart
    );

    configureChart(chart);

    chart.innerRadius = am4core.percent(67);

    chart.data = data.categories || [];

    const series = chart.series.push(
        new am4charts.PieSeries()
    );

    series.dataFields.value = "value";

    series.dataFields.category = "category";

    series.slices.template.stroke =
        am4core.color("#ffffff");

    series.slices.template.strokeWidth = 2;

    series.slices.template.strokeOpacity = 0.85;

    series.slices.template.tooltipText =
        "{category}: [bold]{value}[/]";

    series.labels.template.disabled = true;

    series.ticks.template.disabled = true;

    series.colors.list = [
        chartColour(color),
        am4core.color("#3F77B5"),
        am4core.color("#20A58B"),
        am4core.color("#E7A03B"),
        am4core.color("#9160C7"),
        am4core.color("#D96775")
    ];

    configureTooltip(series.tooltip);

    return chart;
}


//========================================================
// Stack - Stacked Building Columns
//========================================================

function createStackChart(container, data, color)
{
    const chart = am4core.create(
        container,
        am4charts.XYChart
    );

    configureChart(chart);

    const categories = data.categories || [];

    const buildings =
        (data.buildings || []).slice(0, 6);

    chart.data = buildings.map(
        function(building)
        {
            const row = {
                category: building.category
            };

            (building.breakdown || []).forEach(
                function(item, index)
                {
                    row["series" + index] = item.value;
                }
            );

            return row;
        }
    );

    //--------------------------------------------------------
    // Category Axis
    //--------------------------------------------------------

    const categoryAxis = chart.xAxes.push(
        new am4charts.CategoryAxis()
    );

    categoryAxis.dataFields.category = "category";

    configureCategoryAxis(categoryAxis);

    categoryAxis.renderer.labels.template.rotation = -25;

    categoryAxis.renderer.labels.template.horizontalCenter =
        "right";

    categoryAxis.renderer.labels.template.verticalCenter =
        "middle";


    //--------------------------------------------------------
    // Value Axis
    //--------------------------------------------------------

    const valueAxis = chart.yAxes.push(
        new am4charts.ValueAxis()
    );

    configureValueAxis(valueAxis);


    //--------------------------------------------------------
    // Series Colours
    //--------------------------------------------------------

    const colours = [
        color,
        "#3478CB",
        "#20A58B",
        "#E7A03B",
        "#9160C7",
        "#D96775"
    ];


    //--------------------------------------------------------
    // Stacked Series
    //--------------------------------------------------------

    categories.forEach(
        function(category, index)
        {
            const series = chart.series.push(
                new am4charts.ColumnSeries()
            );

            series.name = category.category;

            series.dataFields.categoryX = "category";

            series.dataFields.valueY = "series" + index;

            series.stacked = true;

            series.columns.template.width =
                am4core.percent(65);

            const seriesColor =
                colours[index % colours.length];

            //------------------------------------------------
            // Native v4 Gradient Fill
            //------------------------------------------------

            series.columns.template.fill =
                createBarGradient(seriesColor);

            series.columns.template.stroke =
                am4core.color("#243247");

            series.columns.template.strokeOpacity = 0.22;

            series.columns.template.strokeWidth = 0.6;

            series.columns.template.tooltipText =
                category.category +
                ": [bold]{valueY}[/]";

            configureTooltip(series.tooltip);
        }
    );

    return chart;
}


//========================================================
// Trend - Monthly Line Chart
//========================================================

function createTrendChart(container, data, color)
{
    const chart = am4core.create(
        container,
        am4charts.XYChart
    );

    configureChart(chart);

    chart.data = data.trend || [];

    //--------------------------------------------------------
    // Category Axis
    //--------------------------------------------------------

    const categoryAxis = chart.xAxes.push(
        new am4charts.CategoryAxis()
    );

    categoryAxis.dataFields.category = "category";

    configureCategoryAxis(categoryAxis);

    categoryAxis.renderer.minGridDistance = 25;


    //--------------------------------------------------------
    // Value Axis
    //--------------------------------------------------------

    const valueAxis = chart.yAxes.push(
        new am4charts.ValueAxis()
    );

    configureValueAxis(valueAxis);


    //--------------------------------------------------------
    // Line Series
    //--------------------------------------------------------

    const series = chart.series.push(
        new am4charts.LineSeries()
    );

    series.dataFields.categoryX = "category";

    series.dataFields.valueY = "value";

    series.stroke = chartColour(color);

    series.strokeWidth = 3.5;

    series.tensionX = 0.75;

    series.fill = chartColour(color);

    series.fillOpacity = 0.09;

    series.tooltipText =
        "{categoryX}: [bold]{valueY}[/]";


    //--------------------------------------------------------
    // Data Point Markers
    //--------------------------------------------------------

    const bullet = series.bullets.push(
        new am4charts.CircleBullet()
    );

    bullet.circle.radius = 4;

    bullet.circle.fill = chartColour(color);

    bullet.circle.stroke =
        am4core.color("#ffffff");

    bullet.circle.strokeWidth = 2;

    configureTooltip(series.tooltip);

    return chart;
}


//========================================================
// Top Ten - Horizontal Bars
//========================================================

function createTopTenChart(container, data, color)
{
    const chart = am4core.create(
        container,
        am4charts.XYChart
    );

    configureChart(chart);

    chart.data =
        (data.topTen || []).slice(0, 10);


    //--------------------------------------------------------
    // Category Axis
    //--------------------------------------------------------

    const categoryAxis = chart.yAxes.push(
        new am4charts.CategoryAxis()
    );

    categoryAxis.dataFields.category = "category";

    configureCategoryAxis(categoryAxis);

    categoryAxis.renderer.inversed = true;

    categoryAxis.renderer.minGridDistance = 10;

    categoryAxis.renderer.labels.template.fontSize = 9;


    //--------------------------------------------------------
    // Value Axis
    //--------------------------------------------------------

    const valueAxis = chart.xAxes.push(
        new am4charts.ValueAxis()
    );

    configureValueAxis(valueAxis);


    //--------------------------------------------------------
    // Column Series
    //--------------------------------------------------------

    const series = chart.series.push(
        new am4charts.ColumnSeries()
    );

    series.dataFields.categoryY = "category";

    series.dataFields.valueX = "value";

    series.columns.template.height =
        am4core.percent(65);


    //--------------------------------------------------------
    // Native v4 Gradient Fill
    //--------------------------------------------------------

    series.columns.template.fill =
        createBarGradient(color);

    series.columns.template.stroke =
        am4core.color("#243247");

    series.columns.template.strokeOpacity = 0.22;

    series.columns.template.strokeWidth = 0.7;

    series.columns.template.cornerRadiusTopRight = 2;

    series.columns.template.cornerRadiusBottomRight = 2;

    series.columns.template.tooltipText =
        "{categoryY}: [bold]{valueX}[/]";

    configureTooltip(series.tooltip);

    return chart;
}


//========================================================
// Spider - Radar Chart
//========================================================

function createSpiderChart(container, data, color)
{
    const chart = am4core.create(
        container,
        am4charts.RadarChart
    );

    configureChart(chart);

    chart.data = data.spider || [];

    chart.startAngle = -90;

    chart.endAngle = 270;

    chart.innerRadius = am4core.percent(8);


    //--------------------------------------------------------
    // Category Axis
    //--------------------------------------------------------

    const categoryAxis = chart.xAxes.push(
        new am4charts.CategoryAxis()
    );

    categoryAxis.dataFields.category = "category";

    configureCategoryAxis(categoryAxis);

    categoryAxis.renderer.labels.template.fontSize = 10;

    categoryAxis.renderer.labels.template.maxWidth = 95;


    //--------------------------------------------------------
    // Value Axis
    //--------------------------------------------------------

    const valueAxis = chart.yAxes.push(
        new am4charts.ValueAxis()
    );

    configureValueAxis(valueAxis);

    valueAxis.renderer.labels.template.disabled = true;


    //--------------------------------------------------------
    // Radar Series
    //--------------------------------------------------------

    const series = chart.series.push(
        new am4charts.RadarSeries()
    );

    series.dataFields.categoryX = "category";

    series.dataFields.valueY = "value";

    series.stroke = chartColour(color);

    series.strokeWidth = 3;

    series.fill = chartColour(color);

    series.fillOpacity = 0.28;

    series.tooltipText =
        "{categoryX}: [bold]{valueY}[/]";


    //--------------------------------------------------------
    // Radar Markers
    //--------------------------------------------------------

    const bullet = series.bullets.push(
        new am4charts.CircleBullet()
    );

    bullet.circle.radius = 4;

    bullet.circle.fill = chartColour(color);

    bullet.circle.stroke =
        am4core.color("#ffffff");

    bullet.circle.strokeWidth = 1.5;

    configureTooltip(series.tooltip);

    return chart;
}


//========================================================
// Funnel - Category Distribution
//========================================================

function createFunnelChart(container, data, color)
{
    const chart = am4core.create(
        container,
        am4charts.SlicedChart
    );

    configureChart(chart);

    chart.data = data.funnel || [];

    const series = chart.series.push(
        new am4charts.FunnelSeries()
    );

    series.dataFields.value = "value";

    series.dataFields.category = "category";

    series.slices.template.stroke =
        am4core.color("#ffffff");

    series.slices.template.strokeOpacity = 0.75;

    series.slices.template.strokeWidth = 1.5;

    series.slices.template.tooltipText =
        "{category}: [bold]{value}[/]";

    series.labels.template.fontSize = 10;

    series.labels.template.fontWeight = "500";

    series.labels.template.fill =
        am4core.color("#435466");

    series.labels.template.truncate = true;

    series.labels.template.maxWidth = 105;

    series.colors.list = [
        chartColour(color),
        am4core.color("#3478CB"),
        am4core.color("#20A58B"),
        am4core.color("#E7A03B"),
        am4core.color("#9160C7"),
        am4core.color("#D96775")
    ];

    configureTooltip(series.tooltip);

    return chart;
}


//========================================================
// React Chart Component
//========================================================

export default function HomeIntelligenceChart({
    data,
    chartType = "Total",
    color = "#2686cf"
})
{
    const containerRef = useRef(null);

    const chartRef = useRef(null);


    //========================================================
    // Create / Replace Chart
    //========================================================

    useEffect(
        function()
        {
            if(!containerRef.current || !data)
            {
                return;
            }

            const type =
                String(chartType).toLowerCase();

            let chart = null;

            switch(type)
            {
                case "total":

                    chart = createTotalChart(
                        containerRef.current,
                        data,
                        color
                    );

                    break;

                case "stack":

                    chart = createStackChart(
                        containerRef.current,
                        data,
                        color
                    );

                    break;

                case "trend":

                    chart = createTrendChart(
                        containerRef.current,
                        data,
                        color
                    );

                    break;

                case "top ten":
                case "topten":

                    chart = createTopTenChart(
                        containerRef.current,
                        data,
                        color
                    );

                    break;

                case "spider":

                    chart = createSpiderChart(
                        containerRef.current,
                        data,
                        color
                    );

                    break;

                case "funnel":

                    chart = createFunnelChart(
                        containerRef.current,
                        data,
                        color
                    );

                    break;

                default:

                    chart = createTotalChart(
                        containerRef.current,
                        data,
                        color
                    );

                    break;
            }

            chartRef.current = chart;

            return function()
            {
                if(chart)
                {
                    chart.dispose();
                }

                if(chartRef.current === chart)
                {
                    chartRef.current = null;
                }
            };

        },
        [data, chartType, color]
    );


    //========================================================
    // Render Chart Container
    //========================================================

    return (

        <div
            ref={containerRef}
            style={{
                width: "100%",
                height: "100%",
                minHeight: 0
            }}
        />

    );
}
